import React, { useState, useEffect } from 'react';
import { formatDateTime } from '../../utils/dateUtils';
import api from '../../services/api';

/**
 * Página para gerenciar solicitações de cancelamento de pedidos concluídos
 * Permite aprovar/rejeitar solicitações e visualizar histórico
 */
const GerenciarSolicitacoesCancelamento = () => {
  const [solicitacoes, setSolicitacoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processando, setProcessando] = useState({});
  const [filtros] = useState({
    status: 'Pendente'
  });
  const [historico, setHistorico] = useState([]);
  const [showHistorico, setShowHistorico] = useState(false);
  const [stats, setStats] = useState(null);
  const [expandedSolicitacoes, setExpandedSolicitacoes] = useState(new Set());
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    carregarSolicitacoes();
    carregarEstatisticas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const carregarSolicitacoes = async () => {
    setLoading(true);
    try {
      const response = await api.get('/cancelamento-concluido/solicitacoes', {
        params: filtros
      });
      console.log(response.data.data);
      setSolicitacoes(response.data.data || []);
    } catch (error) {
      console.error('Erro ao carregar solicitações:', error);
      setSolicitacoes([]);
    } finally {
      setLoading(false);
    }
  };

  const carregarHistorico = async () => {
    try {
      // Carregar todas as solicitações (não apenas pendentes) para o histórico
      const response = await api.get('/cancelamento-concluido/cancellation-history', {
        params: { 
          limit: 50,
          // Não especificar status para pegar todas as solicitações
        }
      });
      
      // Garantir que response.data.data é um array e todos os itens têm as propriedades necessárias
      const historicoData = Array.isArray(response.data.data) ? response.data.data : [];
      setHistorico(historicoData);
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
      setHistorico([]); // Garantir que histórico seja um array vazio em caso de erro
    }
  };

  const carregarEstatisticas = async () => {
    try {
      const response = await api.get('/cancelamento-concluido/estatisticas', {
        params: { periodo: 30 }
      });
      setStats(response.data.data);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  };

  const processarSolicitacao = async (solicitacaoId, aprovada, justificativa = '') => {
    if (!aprovada && !justificativa.trim()) {
      alert('Justificativa é obrigatória para rejeição');
      return;
    }

    setProcessando(prev => ({ ...prev, [solicitacaoId]: true }));

    try {
      await api.put(`/cancelamento-concluido/processar/${solicitacaoId}`, {
        aprovada,
        justificativa: justificativa.trim()
      });

      const acao = aprovada ? 'aprovada' : 'rejeitada';
      const icon = aprovada ? '✅' : '❌';
      showNotification(`${icon} Solicitação ${acao} com sucesso!`, 'success');
      
      // Recarregar dados
      await carregarSolicitacoes();
      await carregarEstatisticas();
      
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Erro ao processar solicitação';
      showNotification(`❌ ${errorMessage}`, 'error');
    } finally {
      setProcessando(prev => ({ ...prev, [solicitacaoId]: false }));
    }
  };

  const toggleSolicitacao = (solicitacaoId) => {
    setExpandedSolicitacoes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(solicitacaoId)) {
        newSet.delete(solicitacaoId);
      } else {
        newSet.add(solicitacaoId);
      }
      return newSet;
    });
  };

  const expandirTodas = () => {
    const allIds = new Set(solicitacoes.map(s => s.id));
    setExpandedSolicitacoes(allIds);
  };

  const colapsarTodas = () => {
    setExpandedSolicitacoes(new Set());
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000); // Remove após 5 segundos
  };

  const formatarMoeda = (valor) => {
    if (!valor || isNaN(valor)) return 'R$ 0,00';
    const numericValue = typeof valor === 'string' ? parseFloat(valor) : valor;
    return numericValue.toLocaleString('pt-BR', { 
      style: 'currency', 
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const calcularDiasPendente = (dataSolicitacao) => {
    if (!dataSolicitacao) return 0;
    
    try {
      let dataObj;
      
      // Para datas do banco no formato "YYYY-MM-DD HH:mm:ss"
      if (typeof dataSolicitacao === 'string' && dataSolicitacao.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/)) {
        const [datePart] = dataSolicitacao.split(' ');
        const [year, month, day] = datePart.split('-');
        dataObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      } else {
        dataObj = new Date(dataSolicitacao);
      }
      
      const hoje = new Date();
      const diffTime = hoje - dataObj;
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      return Math.max(0, diffDays);
    } catch (error) {
      return 0;
    }
  };

  const SolicitacaoCard = ({ solicitacao }) => {
    const [justificativa, setJustificativa] = useState('');
    const [showDetalhes, setShowDetalhes] = useState(false);
    const isExpanded = expandedSolicitacoes.has(solicitacao.id);

    const diasPendente = calcularDiasPendente(solicitacao.created_at);
    const isUrgente = diasPendente >= 3;

    return (
      <div className={`border rounded-lg ${isUrgente ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-white'}`}>
        {/* Cabeçalho clicável */}
        <div 
          className="p-3 sm:p-4 cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => toggleSolicitacao(solicitacao.id)}
        >
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
            <div className="flex items-start sm:items-center gap-3">
              <span className="text-lg sm:text-xl">
                {isExpanded ? '🔽' : '▶️'}
              </span>
              <div className="flex-1">
                <h3 className="font-semibold text-base sm:text-lg">
                  Pedido #{solicitacao.pedido_id}
                  {isUrgente && <span className="ml-2 text-red-600 text-xs sm:text-sm">🚨 URGENTE</span>}
                </h3>
                <p className="text-xs sm:text-sm text-gray-600">
                  Cliente: {solicitacao.cliente_nome}
                </p>
                <p className="text-xs sm:text-sm text-gray-600">
                  {diasPendente} dia(s) pendente
                </p>
              </div>
            </div>
            <div className="text-left sm:text-right">
              <div className="text-lg sm:text-lg font-bold text-blue-600">
                {formatarMoeda(solicitacao.pedido_valor)}
              </div>
              <div className="text-xs text-gray-500">
                Solicitado por: {solicitacao.solicitante_nome}
              </div>
            </div>
          </div>
        </div>

        {/* Conteúdo expansível */}
        {isExpanded && (
          <div className="px-3 sm:px-4 pb-3 sm:pb-4 border-t bg-gray-50">

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4 mb-3 text-xs sm:text-sm">
          <div className="p-2 bg-white rounded">
            <strong>Solicitante:</strong> {solicitacao.solicitante_nome}
          </div>
          <div className="p-2 bg-white rounded">
            <strong>Data da solicitação:</strong> {formatDateTime(solicitacao.created_at)}
          </div>
          <div className="p-2 bg-white rounded">
            <strong>Pedido pago em:</strong> {formatDateTime(solicitacao.pagoEm)}
          </div>
          <div className="p-2 bg-white rounded">
            <strong>Pedido concluído em:</strong> {formatDateTime(solicitacao.concluidoEm)}
          </div>
        </div>

        <div className="mb-4 p-3 bg-gray-100 rounded">
          <h4 className="font-medium mb-2 text-sm">Motivo da Solicitação:</h4>
          <p className="text-xs sm:text-sm">{solicitacao.motivo}</p>
        </div>

        <div className="mb-4">
          <button
            onClick={() => setShowDetalhes(!showDetalhes)}
            className="text-xs sm:text-sm text-blue-600 hover:text-blue-800 p-2 bg-blue-50 rounded w-full text-left"
          >
            {showDetalhes ? '▼' : '▶'} Detalhes do Pedido
          </button>
          
          {showDetalhes && (
            <div className="mt-2 p-3 bg-blue-50 rounded text-xs sm:text-sm">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div className="p-2 bg-white rounded"><strong>ID:</strong> {solicitacao.pedido_id}</div>
                <div className="p-2 bg-white rounded"><strong>Valor:</strong> {formatarMoeda(solicitacao.pedido_valor)}</div>
                <div className="p-2 bg-white rounded"><strong>Cliente:</strong> {solicitacao.cliente_nome || 'Não informado'}</div>
                <div className="p-2 bg-white rounded"><strong>Extensão:</strong> {solicitacao.extensao_nome || 'Não informado'}</div>
                <div className="p-2 bg-white rounded"><strong>Status:</strong> Concluído</div>
              </div>
            </div>
          )}
        </div>

        <div className="mb-4">
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
            Justificativa da Decisão:
          </label>
          <textarea
            value={justificativa}
            onChange={(e) => setJustificativa(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            rows="3"
            placeholder="Descreva o motivo da sua decisão..."
          />
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => processarSolicitacao(solicitacao.id, false, justificativa)}
            disabled={processando[solicitacao.id] || !justificativa.trim()}
            className="flex-1 px-4 py-3 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base font-medium"
          >
            {processando[solicitacao.id] ? 'Processando...' : '❌ Rejeitar'}
          </button>
          <button
            onClick={() => processarSolicitacao(solicitacao.id, true, justificativa)}
            disabled={processando[solicitacao.id]}
            className="flex-1 px-4 py-3 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base font-medium"
          >
            {processando[solicitacao.id] ? 'Processando...' : '✅ Aprovar'}
          </button>
        </div>
          </div>
        )}
      </div>
    );
  };

  const EstatisticasCard = ({ stats }) => {
    if (!stats) return null;

    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <div className="bg-blue-100 p-3 sm:p-6 rounded-lg shadow-sm">
          <h3 className="font-semibold text-blue-800 text-xs sm:text-sm mb-1 sm:mb-2">Total de Solicitações</h3>
          <p className="text-xl sm:text-3xl font-bold text-blue-600 mb-1">{stats.solicitacoes?.total_solicitacoes || 0}</p>
          <p className="text-xs sm:text-sm text-blue-600">Últimos {stats.periodo_dias} dias</p>
        </div>
        <div className="bg-green-100 p-3 sm:p-6 rounded-lg shadow-sm">
          <h3 className="font-semibold text-green-800 text-xs sm:text-sm mb-1 sm:mb-2">Cancelamentos Diretos</h3>
          <p className="text-xl sm:text-3xl font-bold text-green-600 mb-1">{stats.cancelamentos_diretos?.total_cancelamentos_diretos || 0}</p>
          <p className="text-xs sm:text-sm text-green-600">Com permissão</p>
        </div>
        <div className="bg-yellow-100 p-3 sm:p-6 rounded-lg shadow-sm">
          <h3 className="font-semibold text-yellow-800 text-xs sm:text-sm mb-1 sm:mb-2">Pendentes</h3>
          <p className="text-xl sm:text-3xl font-bold text-yellow-600 mb-1">{stats.solicitacoes?.pendentes || 0}</p>
          <p className="text-xs sm:text-sm text-yellow-600">Aguardando aprovação</p>
        </div>
        <div className="bg-purple-100 p-3 sm:p-6 rounded-lg shadow-sm">
          <h3 className="font-semibold text-purple-800 text-xs sm:text-sm mb-1 sm:mb-2">Taxa de Aprovação</h3>
          <p className="text-xl sm:text-3xl font-bold text-purple-600 mb-1">
            {stats.solicitacoes?.total_solicitacoes > 0 
              ? Math.round((stats.solicitacoes?.aprovadas / stats.solicitacoes?.total_solicitacoes) * 100)
              : 0
            }%
          </p>
          <p className="text-xs sm:text-sm text-purple-600">
            {stats.solicitacoes?.aprovadas || 0}/{stats.solicitacoes?.total_solicitacoes || 0}
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full min-h-screen bg-gray-50">
      <div className="w-full max-w-none px-3 sm:px-6 py-4 sm:py-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6 gap-3">
          <h1 className="text-xl sm:text-2xl font-bold">Solicitações de Cancelamento</h1>
          <div className="flex gap-2 sm:gap-3">
          <button
            onClick={() => {
              setShowHistorico(!showHistorico);
              if (!showHistorico) carregarHistorico();
            }}
            className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm sm:text-base"
          >
            {showHistorico ? 'Ocultar' : 'Ver'} Histórico
          </button>
          <button
            onClick={() => {
              carregarSolicitacoes();
              carregarEstatisticas();
            }}
            className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 text-sm sm:text-base"
          >
            🔄 Atualizar
          </button>
        </div>
      </div>

      {/* Estatísticas */}
      <EstatisticasCard stats={stats} />

      {/* Lista de Solicitações Pendentes */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 gap-3">
          <h2 className="text-lg sm:text-xl font-semibold">
            Solicitações Pendentes ({solicitacoes.length})
          </h2>
          {solicitacoes.length > 0 && (
            <div className="flex gap-2">
              <button
                onClick={expandirTodas}
                className="flex-1 sm:flex-none px-3 py-2 text-xs sm:text-sm bg-green-100 text-green-700 rounded hover:bg-green-200"
              >
                📖 Expandir Todas
              </button>
              <button
                onClick={colapsarTodas}
                className="flex-1 sm:flex-none px-3 py-2 text-xs sm:text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
              >
                📕 Minimizar Todas
              </button>
            </div>
          )}
        </div>
        
        {loading ? (
          <div className="text-center py-8">Carregando solicitações...</div>
        ) : solicitacoes.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            🎉 Nenhuma solicitação pendente no momento
          </div>
        ) : (
          <div className="space-y-4">
            {solicitacoes.map(solicitacao => (
              <SolicitacaoCard key={solicitacao.id} solicitacao={solicitacao} />
            ))}
          </div>
        )}
      </div>

      {/* Histórico */}
      {showHistorico && (
        <div>
          <h2 className="text-lg sm:text-xl font-semibold mb-4">
            Histórico de Operações ({historico.length})
          </h2>
          
          <div className="bg-white border rounded-lg overflow-hidden">
            {/* Versão mobile - Cards */}
            <div className="block sm:hidden">
              <div className="space-y-3 p-3">
                {historico.filter(item => item && typeof item === 'object').map((item, index) => (
                  <div key={index} className="border rounded-lg p-3 bg-gray-50">
                    <div className="flex justify-between items-start mb-2">
                      <div className="text-sm font-semibold">#{item.pedido_id || 'N/A'}</div>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        item.status === 'Aprovada' ? 'bg-green-100 text-green-800' :
                        item.status === 'Rejeitada' ? 'bg-red-100 text-red-800' :
                        item.status === 'Pendente' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {item.status || 'N/A'}
                      </span>
                    </div>
                    <div className="text-xs text-gray-600 space-y-1">
                      <div><strong>Data:</strong> {item.created_at ? formatDateTime(item.created_at) : 'N/A'}</div>
                      <div><strong>Cliente:</strong> {item.cliente_nome || 'N/A'}</div>
                      <div><strong>Valor:</strong> {formatarMoeda(item.pedido_valor)}</div>
                      <div><strong>Solicitante:</strong> {item.solicitante_nome || 'N/A'}</div>
                      <div><strong>Processado por:</strong> {item.aprovador_nome || (item.status === 'Pendente' ? 'Aguardando' : 'N/A')}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Versão desktop - Tabela */}
            <div className="hidden sm:block">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Data Solicitação
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Pedido
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Cliente
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Valor
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Solicitante
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Processado por
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {historico.filter(item => item && typeof item === 'object').map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.created_at ? formatDateTime(item.created_at) : 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            item.status === 'Aprovada' ? 'bg-green-100 text-green-800' :
                            item.status === 'Rejeitada' ? 'bg-red-100 text-red-800' :
                            item.status === 'Pendente' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {item.status || 'N/A'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          #{item.pedido_id || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.cliente_nome || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatarMoeda(item.pedido_valor)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.solicitante_nome || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.aprovador_nome || (item.status === 'Pendente' ? 'Aguardando' : 'N/A')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Sistema de Notificações */}
      {notification && (
        <div className={`fixed top-4 left-3 right-3 sm:left-auto sm:right-4 sm:w-auto z-50 p-3 sm:p-4 rounded-lg shadow-lg transition-all duration-300 ${
          notification.type === 'success' 
            ? 'bg-green-500 text-white' 
            : 'bg-red-500 text-white'
        }`}>
          <div className="flex items-center justify-between">
            <span className="font-medium text-sm sm:text-base">{notification.message}</span>
            <button 
              onClick={() => setNotification(null)}
              className="ml-4 text-white hover:text-gray-200 transition-colors text-lg"
            >
              ✕
            </button>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default GerenciarSolicitacoesCancelamento;
